import { Button, IconButton, Tooltip } from '@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Undo as UndoIcon,
} from '@mui/icons-material';
import { ReactElement, useState } from 'react';
import { useBackdrop } from './useBackdrop';

export interface IInlineEditor<T> {
  value: T | undefined;
  setValue: React.Dispatch<React.SetStateAction<T | undefined>>;
  editing: boolean;
  setEditing: React.Dispatch<React.SetStateAction<boolean>>;
  editButton: false | ReactElement;
  cancelButton: false | ReactElement;
  saveButton: false | ReactElement;
}

export function useInlineEditor<T, U>(
  onSave: (value: T) => Promise<U>,
  savedValue?: T,
  validate?: (value?: T) => boolean
): IInlineEditor<T> {
  const withBackdrop = useBackdrop();

  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(savedValue);

  async function saveChanges() {
    await withBackdrop(async () => {
      await onSave(value as T);
      setEditing(false);
    });
  }
  function cancelEditing() {
    setEditing(false);
    setValue(savedValue);
  }

  return {
    value,
    setValue,
    editing,
    setEditing,
    editButton: !editing && (
      <Tooltip enterDelay={800} title="Edit">
        <IconButton
          aria-label="Edit"
          onClick={() => setEditing(true)}
          size="small"
          sx={{ height: 44, width: 44 }}
        >
          <EditIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    ),
    cancelButton: editing && (
      <Button
        onClick={() => cancelEditing()}
        variant="contained"
        size="small"
        startIcon={<UndoIcon />}
        color="secondary"
        sx={{ margin: 1 }}
      >
        Cancel
      </Button>
    ),
    saveButton: editing && (
      <Button
        disabled={
          value === savedValue ||
          typeof value === 'undefined' ||
          (typeof validate !== 'undefined' && !validate(value))
        }
        onClick={saveChanges}
        variant="contained"
        size="small"
        startIcon={<SaveIcon />}
        sx={{ margin: 1 }}
      >
        Save
      </Button>
    ),
  };
}
